import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { Chip, ConfidenceBadge } from '../components/Chips';
import { SelectField, TextField } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../lib/auth';
import { documents, purchaseOrders, transactions } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { DocumentStatus, QboEntityType } from '../lib/enums';
import { formatDate, formatMoney, formatRelative } from '../lib/format';
import type { QboTransactionDto } from '../lib/types';

type PurchaseOrderColumn = 'docNumber' | 'vendor' | 'date' | 'total' | 'status' | 'linked' | 'synced';

function purchaseOrderSortValue(po: QboTransactionDto, key: PurchaseOrderColumn): string | number {
  switch (key) {
    case 'docNumber':
      return po.docNumber ?? '';
    case 'vendor':
      return po.vendorName ?? '';
    case 'date':
      return po.txnDate ?? '';
    case 'total':
      return po.totalAmount ?? 0;
    case 'status':
      return po.poStatus ?? '';
    case 'linked':
      return po.linkedToGateway ? 1 : 0;
    case 'synced':
      return po.lastSyncedUtc ?? '';
    default:
      return '';
  }
}

/**
 * Purchase orders cached from QuickBooks, plus the panel that links a document to one of them.
 *
 * Linking creates the bill and points it at the purchase order through LinkedTxn. Nothing compares
 * invoice lines to purchase order lines, quantities, prices or items, so the UI says "link" rather
 * than "match" throughout. The candidate list is genuinely ranked, so it stays — as suggestions for
 * which purchase order to link to.
 */
export function PurchaseOrdersPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canPull = can('transactions.pull');
  const canLink = can('purchaseOrders.match');

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDocumentId = searchParams.get('document') ?? '';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pulling, setPulling] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const debouncedSearch = useDebounced(search, 350);

  const list = useAsync(
    (signal) =>
      transactions.list(
        { entityType: QboEntityType.PurchaseOrder, search: debouncedSearch, page, pageSize },
        signal,
      ),
    [debouncedSearch, page, pageSize],
  );

  const poTable = useSortableTable<PurchaseOrderColumn>({
    storageKey: 'purchaseOrdersColumnWidths',
    columns: {
      docNumber: { minWidth: 130 },
      vendor: { minWidth: 140 },
      date: { minWidth: 100 },
      total: { minWidth: 90 },
      status: { minWidth: 100 },
      linked: { minWidth: 90 },
      synced: { minWidth: 100 },
    },
  });

  // Candidate documents to link: anything still waiting on a human decision.
  const candidates = useAsync(
    (signal) =>
      documents.list({ status: DocumentStatus.ReadyForReview, page: 1, pageSize: 100 }, signal),
    [],
  );

  const suggestions = useAsync(
    (signal) =>
      selectedDocumentId
        ? purchaseOrders.suggestions(selectedDocumentId, signal)
        : Promise.resolve([]),
    [selectedDocumentId],
  );

  const selectDocument = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('document', id);
    else next.delete('document');
    setSearchParams(next, { replace: true });
  };

  const pull = async () => {
    setPulling(true);
    try {
      const response = await transactions.pull({
        entityType: QboEntityType.PurchaseOrder,
        changedSinceUtc: null,
        maxRecords: null,
      });
      toast.success(
        'Purchase orders pulled',
        `${response.recordsPulled} read · ${response.recordsCreated} new · ${response.recordsUpdated} updated.`,
      );
      list.reload();
    } catch (error) {
      toast.error('Pull failed', errorMessage(error));
    } finally {
      setPulling(false);
    }
  };

  const link = async (purchaseOrderQboId: string) => {
    if (!selectedDocumentId) return;
    setLinking(purchaseOrderQboId);
    try {
      // The API route is still /purchase-orders/match; only the wording the user sees changes.
      const response = await purchaseOrders.match({
        documentId: selectedDocumentId,
        purchaseOrderQboId,
      });
      if (response?.success) {
        toast.success(
          'Linked to purchase order',
          response.qboDocNumber ? `Linked to ${response.qboDocNumber}.` : (response.message ?? undefined),
        );
        response.warnings?.forEach((warning) => toast.warn('Linking warning', warning));
        suggestions.reload();
        list.reload();
        candidates.reload();
      } else {
        toast.error('Linking failed', response?.message ?? 'QuickBooks rejected the link.');
      }
    } catch (error) {
      toast.error('Linking failed', errorMessage(error));
    } finally {
      setLinking(null);
    }
  };

  const candidateOptions = (candidates.data?.items ?? []).map((doc) => ({
    value: doc.id,
    label: `${doc.vendorName ?? 'Unknown vendor'} · ${doc.invoiceNumber ?? doc.fileName} · ${formatMoney(doc.total, doc.currencyCode)}`,
  }));

  return (
    <>
      <PageHeader
        title="Purchase Orders"
        description="Cached QuickBooks purchase orders, and linking a document to one of them."
        actions={
          canPull ? (
            <button type="button" className="btn btn-secondary" onClick={pull} disabled={pulling}>
              {pulling ? 'Pulling…' : 'Pull from QuickBooks'}
            </button>
          ) : undefined
        }
      />

      <Panel
        title="Link to purchase order"
        description="Pick a document waiting for review, then link it to one of the suggested purchase orders. The bill is created and references the purchase order; its lines are not compared to the purchase order's."
      >
        <Toolbar>
          <SelectField
            label="Document"
            value={selectedDocumentId}
            onChange={selectDocument}
            options={candidateOptions}
            placeholder={candidates.loading ? 'Loading documents…' : 'Select a document'}
            className="toolbar-grow"
            disabled={candidates.loading}
          />
        </Toolbar>

        {!selectedDocumentId ? (
          <EmptyState
            title="No document selected"
            description="Choose a document above to see suggested purchase orders to link it to."
          />
        ) : suggestions.loading ? (
          <TableSkeleton rows={3} columns={5} />
        ) : suggestions.error ? (
          <ErrorState
            message="Suggestions could not be loaded."
            detail={suggestions.error}
            onRetry={suggestions.reload}
          />
        ) : !suggestions.data || suggestions.data.length === 0 ? (
          <EmptyState
            title="No purchase orders to suggest"
            description="The gateway found no open purchase order that resembles this document."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th scope="col">Purchase order</th>
                <th scope="col">Vendor</th>
                <th scope="col">Date</th>
                <th scope="col" className="num">
                  Total
                </th>
                <th scope="col">Confidence</th>
                <th scope="col">Why</th>
                <th scope="col" className="col-actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {suggestions.data.map((suggestion) => (
                <tr key={suggestion.purchaseOrderQboId}>
                  <td>
                    <span className="cell-strong">{suggestion.docNumber ?? '—'}</span>
                    <span className="cell-sub mono">{suggestion.purchaseOrderQboId}</span>
                  </td>
                  <td>{suggestion.vendorName ?? '—'}</td>
                  <td>{formatDate(suggestion.txnDate)}</td>
                  <td className="num">{formatMoney(suggestion.totalAmount)}</td>
                  <td>
                    <ConfidenceBadge value={suggestion.confidence} size="sm" />
                  </td>
                  <td>{suggestion.reason}</td>
                  <td className="col-actions">
                    {canLink ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => link(suggestion.purchaseOrderQboId)}
                        disabled={linking !== null}
                      >
                        {linking === suggestion.purchaseOrderQboId ? 'Linking…' : 'Link'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      <Panel title="Cached purchase orders">
        <Toolbar>
          <TextField
            label="Search"
            type="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Document number, vendor or memo"
            className="toolbar-grow"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={8} columns={6} />
        ) : list.error ? (
          <ErrorState message="Purchase orders could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="No purchase orders cached"
            description="Pull from QuickBooks to populate the cache."
            action={
              canPull ? (
                <button type="button" className="btn btn-primary" onClick={pull} disabled={pulling}>
                  Pull from QuickBooks
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={poTable.columnStyle('docNumber')} />
                <col style={poTable.columnStyle('vendor')} />
                <col style={poTable.columnStyle('date')} />
                <col style={poTable.columnStyle('total')} />
                <col style={poTable.columnStyle('status')} />
                <col style={poTable.columnStyle('linked')} />
                <col style={poTable.columnStyle('synced')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="PO number"
                    sortKey="docNumber"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('docNumber')}
                    onResizeStart={poTable.beginResize('docNumber')}
                  />
                  <SortableTh
                    label="Vendor"
                    sortKey="vendor"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('vendor')}
                    onResizeStart={poTable.beginResize('vendor')}
                  />
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('date')}
                    onResizeStart={poTable.beginResize('date')}
                  />
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    className="num"
                    style={poTable.columnStyle('total')}
                    onResizeStart={poTable.beginResize('total')}
                  />
                  <SortableTh
                    label="PO status"
                    sortKey="status"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('status')}
                    onResizeStart={poTable.beginResize('status')}
                  />
                  <SortableTh
                    label="Linked"
                    sortKey="linked"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('linked')}
                    onResizeStart={poTable.beginResize('linked')}
                  />
                  <SortableTh
                    label="Synced"
                    sortKey="synced"
                    currentSortKey={poTable.sortKey}
                    sortDir={poTable.sortDir}
                    onSort={poTable.toggleSort}
                    style={poTable.columnStyle('synced')}
                    onResizeStart={poTable.beginResize('synced')}
                  />
                </tr>
              </thead>
              <tbody>
                {poTable.sort(list.data.items, purchaseOrderSortValue).map((po) => (
                  <tr key={po.id}>
                    <td>
                      <span className="cell-strong">{po.docNumber ?? '—'}</span>
                      <span className="cell-sub mono">{po.qboId}</span>
                    </td>
                    <td>{po.vendorName ?? '—'}</td>
                    <td>{formatDate(po.txnDate)}</td>
                    <td className="num">{formatMoney(po.totalAmount, po.currencyCode)}</td>
                    <td>{po.poStatus ? <Chip tone="neutral">{po.poStatus}</Chip> : '—'}</td>
                    <td>
                      {po.linkedToGateway ? <Chip tone="good">Linked</Chip> : <Chip tone="neutral">Unlinked</Chip>}
                    </td>
                    <td>{formatRelative(po.lastSyncedUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
